/**
 * Updates the numbering of chapters and subchapters in a report.
 *
 * Top-level chapters are numbered numerically, while appendix chapters are labeled with letters (A, B, ...).
 * Subchapters are numbered based on their parent chapter's number.
 *
 * @param {Report} report - The report object containing chapters to be renumbered.
 * @returns {Report} - The updated report with renumbered chapters.
 *
 * Usage:
 * const updatedReport = updateReportChapterNumbering(report);
 */
import { Chapter, ChapterType, Report, TextBlock } from "graphql/types"
import { v4 } from "uuid"

/**
 * Ensures that Appendix A is always present in the report with its text blocks
 */
export const ensureAppendixA = (report: Report, textBlocks: TextBlock[] = []): Report => {
  const hasAppendixA = report.chapter.some((chapter) => chapter.type === ChapterType.APPENDIX_A)

  if (!hasAppendixA) {
    // Find text blocks for APPENDIX_A
    const appendixATextBlocks = textBlocks.filter(
      (block) => block.chapter === ChapterType.APPENDIX_A
    )

    // Convert text blocks to data nodes
    const dataNodes = appendixATextBlocks.flatMap((textBlock) =>
      textBlock.block.map((block) => ({
        ...block,
        id: v4(), // Generate new ID for each data node
      }))
    )

    const appendixA: Chapter = {
      id: v4(),
      type: ChapterType.APPENDIX_A,
      name: "Applied Conformity Assessment Rule",
      chapterNumber: "Appendix A",
      subChapter: [],
      open: false,
      data: dataNodes,
    }

    return {
      ...report,
      chapter: [...report.chapter, appendixA],
    }
  }

  return report
}

export const updateReportChapterNumbering = (
  report: Report,
  textBlocks: TextBlock[] = []
): Report => {
  // First ensure Appendix A is present with text blocks
  const reportWithAppendixA = ensureAppendixA(report, textBlocks)
  // Top-level chapters: regular chapters get numeric numbering,
  // appendix chapters get Buchstaben (A, B, …)
  const updateTopLevelChapters = (chapters: Chapter[]): Chapter[] => {
    // Separate regular chapters from appendix chapters
    const regularChapters = chapters.filter(
      (chapter) => chapter.type !== ChapterType.APPENDIX && chapter.type !== ChapterType.APPENDIX_A
    )
    const appendixAChapters = chapters.filter((chapter) => chapter.type === ChapterType.APPENDIX_A)
    const appendixChapters = chapters.filter((chapter) => chapter.type === ChapterType.APPENDIX)

    let regularCounter = 1
    let appendixCounter = 1 // Start with B (1 -> B, 2 -> C, ...)

    // Process regular chapters first
    const updatedRegularChapters = regularChapters.map((chapter) => {
      const chapterNumber = `${regularCounter}`
      regularCounter++
      const updatedSubChapters = updateSubChapters(chapter.subChapter ?? [], chapterNumber)
      return {
        ...chapter,
        chapterNumber: chapterNumber,
        subChapter: updatedSubChapters,
      }
    })

    // Process APPENDIX_A chapters (always "Appendix A")
    const updatedAppendixAChapters = appendixAChapters.map((chapter) => {
      const chapterNumber = "Appendix A"
      const updatedSubChapters = updateSubChapters(chapter.subChapter ?? [], chapterNumber)
      return {
        ...chapter,
        chapterNumber: chapterNumber,
        subChapter: updatedSubChapters,
      }
    })

    // Process other appendix chapters (B, C, D, ...)
    const updatedAppendixChapters = appendixChapters.map((chapter) => {
      const appendixLetter = String.fromCharCode(65 + appendixCounter) // 65 = 'A', so 1 -> 'B', 2 -> 'C', etc.
      const chapterNumber = `Appendix ${appendixLetter}`
      appendixCounter++
      const updatedSubChapters = updateSubChapters(chapter.subChapter ?? [], chapterNumber)
      return {
        ...chapter,
        chapterNumber: chapterNumber,
        subChapter: updatedSubChapters,
      }
    })

    // Combine in correct order: regular chapters, then APPENDIX_A, then other appendix chapters
    return [...updatedRegularChapters, ...updatedAppendixAChapters, ...updatedAppendixChapters]
  }

  // Subchapters: Nummerierung beginnt mit der Nummer des Elternkapitels plus fortlaufende Nummern.
  const updateSubChapters = (chapters: Chapter[] | undefined, parentNumber: string): Chapter[] => {
    if (!chapters) return []
    return chapters.map((chapter, index) => {
      const chapterNumber = `${parentNumber}.${index + 1}`
      const updatedSubChapters = updateSubChapters(chapter.subChapter ?? undefined, chapterNumber)
      return {
        ...chapter,
        chapterNumber: chapterNumber,
        subChapter: updatedSubChapters,
      }
    })
  }

  const updatedChapters = updateTopLevelChapters(reportWithAppendixA.chapter)

  return { ...reportWithAppendixA, chapter: updatedChapters }
}
